import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { AlertTriangle, Trash2, CheckCircle2 } from 'lucide-react';

/**
 * Shared confirmation dialog for permanent partner deletion.
 * Props:
 *   target: partner object being deleted ({ id, name, email, client_count? })
 *   confirmText / setConfirmText: controlled text input state
 *   onClose: close handler
 *   onConfirm: confirm handler
 *   isDeleting: boolean loader
 *   result: null or { deleted: {...}, partner_email } after success
 */
const HardDeletePartnerDialog = ({ target, confirmText, setConfirmText, onClose, onConfirm, isDeleting, result }) => {
  if (!target) return null;

  const matches = confirmText.trim().toLowerCase() === (target.email || '').trim().toLowerCase();

  return (
    <Dialog open={!!target} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-slate-900 border-red-500/40 text-white max-w-lg" data-testid="hard-delete-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2 text-red-400">
            <AlertTriangle className="w-5 h-5" />
            {result ? 'Deletion Complete' : 'Permanently Delete Partner'}
          </DialogTitle>
        </DialogHeader>

        {result ? (
          /* Success summary */
          <div className="space-y-4 mt-2">
            <div className="flex items-start gap-3 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-emerald-300 font-medium">
                  {result.message || 'Partner and all related data permanently deleted.'}
                </p>
                <p className="text-emerald-400/70 text-xs mt-1">{result.partner_email}</p>
              </div>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <p className="text-slate-300 text-sm font-semibold mb-3">Records removed</p>
              <ul className="space-y-1.5 text-sm">
                {Object.entries(result.deleted || {}).map(([k, v]) => (
                  <li key={k} className="flex justify-between">
                    <span className="text-slate-400">{k.replace(/_/g, ' ')}</span>
                    <span className="text-white font-mono">{v}</span>
                  </li>
                ))}
              </ul>
            </div>

            <Button onClick={onClose} className="w-full bg-slate-700 hover:bg-slate-600 text-white" data-testid="hard-delete-close">
              Close
            </Button>
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
              <p className="text-red-300 text-sm leading-relaxed">
                This action is <strong>irreversible</strong>. It will permanently delete:
              </p>
              <ul className="text-red-300/80 text-sm list-disc list-inside mt-2 space-y-1">
                <li>The partner record</li>
                <li>All {target.client_count ?? ''} clients under this partner</li>
                <li>All wills, vault entries, and trusted contacts of those clients</li>
                <li>All uploaded vault files on disk</li>
                <li>All payments, payment transactions, brokerage records & consent logs</li>
              </ul>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">Name</span>
                <span className="text-white font-medium">{target.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Email</span>
                <span className="text-white font-mono text-xs">{target.email}</span>
              </div>
              {target.client_count !== undefined && (
                <div className="flex justify-between">
                  <span className="text-slate-400">Clients</span>
                  <span className="text-white">{target.client_count}</span>
                </div>
              )}
            </div>

            <div>
              <Label className="text-slate-300 text-sm mb-2 block">
                Type <span className="text-amber-400 font-mono">{target.email}</span> to confirm
              </Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type the email exactly"
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="hard-delete-confirm-input"
                autoComplete="off"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                onClick={onClose}
                data-testid="hard-delete-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={onConfirm}
                disabled={!matches || isDeleting}
                className="flex-1 bg-red-600 hover:bg-red-500 text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="hard-delete-confirm"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {isDeleting ? 'Deleting...' : 'Delete Permanently'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HardDeletePartnerDialog;
